/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../components/Footer";
import {
    FaUser, FaEnvelope, FaPhone, FaCalendarAlt, FaSpinner,
    FaCheckCircle, FaExclamationTriangle, FaTrophy, FaClock,
    FaChevronLeft, FaChevronRight, FaBuilding, FaMapMarkerAlt,
    FaGlobe, FaBriefcase, FaUserPlus, FaTimesCircle, FaListAlt,
    FaArrowLeft, FaHourglassHalf, FaUserTie, FaBusinessTime,
    FaIndustry, FaStar, FaQuestionCircle, FaAward,
    FaBalanceScale, FaGavel, FaHandshake, FaShieldAlt
} from "react-icons/fa";
import ReCAPTCHA from "react-google-recaptcha";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_AWARDS = `${API_BASE_URL}/UIawards`;
const API_REGISTRATIONS = `${API_BASE_URL}/UI-registrations`;

const AwardRegistration = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [award, setAward] = useState(null);
    const [loading, setLoading] = useState(true);
    const [carouselImages, setCarouselImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [autoPlay, setAutoPlay] = useState(true);
    const [imageLoading, setImageLoading] = useState(true);

    // Form Data State
    const [formData, setFormData] = useState({
        // Basic Details
        fullName: "",
        mobileNo: "",
        email: "",
        city: "",

        // Professional Details
        applicantType: "", // "Business Owner" or "Working Professional"
        companyName: "",
        designation: "",

        // Profile Information
        aboutYou: "",
        aboutBusiness: "",

        // Award Category
        awardCategory: "",

        // Award Nomination Details
        nominationReason: "",

        // NEW: Ethics & Compliance Section
        ethicsDeclaration: false,
        businessPhilosophy: "",
        regulatoryHistory: "no",
        regulatoryExplanation: ""
    });

    const [awardCategories, setAwardCategories] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [errorMessage, setErrorMessage] = useState("");
    const [registrationStatus, setRegistrationStatus] = useState("");
    const [showPendingMessage, setShowPendingMessage] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const [captchaToken, setCaptchaToken] = useState(null);
    const [categoriesLoading, setCategoriesLoading] = useState(false);
    const [firstImageLoaded, setFirstImageLoaded] = useState(false);

    // Fetch award details and categories
    const fetchAwardDetails = async () => {
        try {
            setLoading(true);
            setCategoriesLoading(true);

            // Fetch award details
            const awardRes = await axios.get(`${API_AWARDS}/${id}`);
            const awardData = awardRes.data;
            setAward(awardData);
            checkRegistrationStatus(awardData);
            await processAwardImages(awardData);

            // Fetch all awards to get categories
            await fetchAwardCategories(awardData);

        } catch (err) {
            console.error("Error fetching award details:", err);
            setErrorMessage("Failed to load award details. Please try again.");
        } finally {
            setLoading(false);
            setCategoriesLoading(false);
        }
    };

    // Fetch award categories from all awards
    const fetchAwardCategories = async (currentAward) => {
        try {
            const res = await axios.get(`${API_AWARDS}/${id}`);
            const allAwards = Array.isArray(res.data) ? res.data :
                res.data?.items ? res.data.items :
                    res.data?.data ? res.data.data : [];

            const uniqueCategories = new Set();

            // Add categories from all awards
            allAwards.forEach(award => {
                if (award.awardCategories && Array.isArray(award.awardCategories)) {
                    award.awardCategories.forEach(category => {
                        if (category && typeof category === 'string' && category.trim()) {
                            uniqueCategories.add(category.trim());
                        }
                    });
                }
            });

            // Add current award's categories if not already present
            if (currentAward.awardCategories && Array.isArray(currentAward.awardCategories)) {
                currentAward.awardCategories.forEach(category => {
                    if (category && typeof category === 'string' && category.trim()) {
                        uniqueCategories.add(category.trim());
                    }
                });
            }

            setAwardCategories(Array.from(uniqueCategories).sort());

        } catch (err) {
            console.error("Error fetching award categories:", err);
        }
    };

    // Check registration status
    const checkRegistrationStatus = (awardData) => {
        const now = new Date();
        const regStart = new Date(awardData.registrationStartDate);
        const regEnd = new Date(awardData.registrationEndDate);
        const eventStart = new Date(awardData.awardEventStartDate);

        if (now < regStart) setRegistrationStatus("not-started");
        else if (now >= regStart && now <= regEnd) setRegistrationStatus("open");
        else if (now > regEnd && now < eventStart) setRegistrationStatus("closed");
        else if (now >= eventStart) setRegistrationStatus("completed");
    };

    // Process award images
    const processAwardImages = async (awardData) => {
        if (!awardData.gallery || !Array.isArray(awardData.gallery)) {
            setCarouselImages([{
                url: "https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
                caption: "Award Ceremony",
                isPlaceholder: true
            }]);
            setImageLoading(false);
            return;
        }

        const images = [];
        for (const group of awardData.gallery) {
            if (group.photos?.length) {
                for (const photo of group.photos) {
                    try {
                        const mediaId = photo.mediaId?._id || photo.mediaId?.id || photo.mediaId;
                        if (mediaId) {
                            const imgRes = await axios.get(
                                `${API_BASE_URL}/UImedia/${mediaId}/download`,
                                { responseType: "blob" }
                            );
                            if (imgRes.data) {
                                const imageUrl = URL.createObjectURL(imgRes.data);
                                images.push({
                                    url: imageUrl,
                                    caption: photo.caption || group.groupName || "Award Image",
                                    isPlaceholder: false
                                });
                            }
                        }
                    } catch (err) {
                        console.error("Error loading image:", err);
                    }
                }
            }
        }

        if (!images.length) {
            images.push({
                url: "https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80",
                caption: "Award Ceremony",
                isPlaceholder: true
            });
        }

        setCarouselImages(images);
        setImageLoading(false);
    };

    // Form validation
    const validateForm = () => {
        const requiredFields = [
            'fullName', 'mobileNo', 'email', 'city',
            'applicantType', 'designation', 'awardCategory', 'nominationReason'
        ];

        const missingFields = requiredFields.filter(field => !formData[field].trim());

        if (missingFields.length) {
            setErrorMessage(`Please fill in all required fields: ${missingFields.join(', ')}`);
            return false;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setErrorMessage("Please enter a valid email address.");
            return false;
        }

        // Mobile validation
        const mobileRegex = /^[0-9+\-\s()]{10,}$/;
        if (!mobileRegex.test(formData.mobileNo.replace(/\s/g, ''))) {
            setErrorMessage("Please enter a valid mobile number (minimum 10 digits).");
            return false;
        }

        // Company name validation for both types
        if (!formData.companyName.trim()) {
            setErrorMessage("Please provide Company/Business Name.");
            return false;
        }

        // NEW: Ethics Declaration validation
        if (!formData.ethicsDeclaration) {
            setErrorMessage("You must agree to the Ethics & Compliance Declaration.");
            return false;
        }

        // Business Philosophy validation
        if (!formData.businessPhilosophy) {
            setErrorMessage("Please select your Business Philosophy.");
            return false;
        }

        // Regulatory Explanation validation if Yes is selected
        if (formData.regulatoryHistory === "yes" && !formData.regulatoryExplanation.trim()) {
            setErrorMessage("Please provide explanation for regulatory/legal history.");
            return false;
        }

        // Text length validations
        if (formData.aboutYou && formData.aboutYou.split(/\s+/).length > 200) {
            setErrorMessage("About You should be maximum 200 words.");
            return false;
        }

        if (formData.aboutBusiness && formData.aboutBusiness.split(/\s+/).length > 200) {
            setErrorMessage("About Business should be maximum 200 words.");
            return false;
        }

        if (formData.nominationReason.split(/\s+/).length > 100) {
            setErrorMessage("Nomination reason should be short answer (max 100 words).");
            return false;
        }

        if (formData.regulatoryExplanation.split(/\s+/).length > 100) {
            setErrorMessage("Regulatory explanation should be maximum 100 words.");
            return false;
        }

        return true;
    };

    const handleCaptchaChange = (token) => {
        setCaptchaToken(token);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!award) return;

        if (!captchaToken) {
            setSubmitStatus('error');
            setErrorMessage("Please verify that you are not a robot.");
            return;
        }

        if (registrationStatus !== "open") {
            setErrorMessage("Registration is not currently open for this award.");
            setSubmitStatus('error');
            return;
        }

        if (!validateForm()) {
            setSubmitStatus('error');
            return;
        }

        setIsSubmitting(true);
        setSubmitStatus(null);
        setErrorMessage("");

        try {
            const registrationData = {
                awardId: award._id || award.id,
                awardName: award.awardFullName || award.awardName,
                awardDescription: award.awardDescription,
                awardEventStartDate: award.awardEventStartDate,
                awardEventEndDate: award.awardEventEndDate,
                registrationStartDate: award.registrationStartDate,
                registrationEndDate: award.registrationEndDate,
                eventLocation: award.location || {},

                // Basic Details
                fullName: formData.fullName,
                mobileNo: formData.mobileNo,
                email: formData.email,
                city: formData.city,

                // Professional Details
                applicantType: formData.applicantType,
                companyName: formData.companyName,
                designation: formData.designation,

                // Profile Information
                aboutYou: formData.aboutYou,
                aboutBusiness: formData.aboutBusiness,

                // Award Category
                awardCategory: formData.awardCategory,

                // Award Nomination Details
                nominationReason: formData.nominationReason,

                // NEW: Ethics & Compliance Details
                ethicsDeclaration: formData.ethicsDeclaration,
                businessPhilosophy: formData.businessPhilosophy,
                regulatoryHistory: formData.regulatoryHistory,
                regulatoryExplanation: formData.regulatoryExplanation,

                captchaToken: captchaToken,
                registrationDate: new Date().toISOString(),
                status: "pending",
                isActive: true
            };

            const response = await axios.post(API_REGISTRATIONS, registrationData, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200 || response.status === 201) {
                setSubmitStatus('pending');
                setShowPendingMessage(true);
                setCountdown(30);
                setCaptchaToken(null);

                // Start countdown
                const countdownInterval = setInterval(() => {
                    setCountdown(prev => {
                        if (prev <= 1) {
                            clearInterval(countdownInterval);
                            setShowPendingMessage(false);
                            navigate("/MASAAwards");
                            return 0;
                        }
                        return prev - 1;
                    });
                }, 1000);

                // Reset form
                setFormData({
                    fullName: "",
                    mobileNo: "",
                    email: "",
                    city: "",
                    applicantType: "",
                    companyName: "",
                    designation: "",
                    aboutYou: "",
                    aboutBusiness: "",
                    awardCategory: "",
                    nominationReason: "",
                    ethicsDeclaration: false,
                    businessPhilosophy: "",
                    regulatoryHistory: "no",
                    regulatoryExplanation: ""
                });
            }
        } catch (error) {
            console.error("Registration error:", error);
            setSubmitStatus('error');
            setErrorMessage(
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                "Failed to submit registration. Please try again."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Handle textarea word count
    const handleTextareaChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const getWordCount = (text) => {
        return text ? text.trim().split(/\s+/).filter(word => word.length > 0).length : 0;
    };

    // Carousel navigation
    const nextImage = () => {
        setDirection(1);
        setCurrentImageIndex(prev => prev === carouselImages.length - 1 ? 0 : prev + 1);
    };

    const prevImage = () => {
        setDirection(-1);
        setCurrentImageIndex(prev => prev === 0 ? carouselImages.length - 1 : prev - 1);
    };

    const goToImage = (index) => {
        setDirection(index > currentImageIndex ? 1 : -1);
        setAutoPlay(false);
        setCurrentImageIndex(index);
        setTimeout(() => setAutoPlay(true), 10000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString('en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
            });
        } catch (error) {
            return "Invalid Date";
        }
    };

    const handleBackToAwards = () => navigate("/MASAAwards");

    // Get registration status message
    const getRegistrationStatusMessage = () => {
        const statusConfig = {
            "not-started": {
                message: `Registration starts on ${formatDate(award?.registrationStartDate)}`,
                icon: FaClock,
                color: "text-blue-400",
                bgColor: "bg-blue-900/20",
                borderColor: "border-blue-800"
            },
            "open": {
                message: `Registration open until ${formatDate(award?.registrationEndDate)}`,
                icon: FaUserPlus,
                color: "text-green-400",
                bgColor: "bg-green-900/20",
                borderColor: "border-green-800"
            },
            "closed": {
                message: "Registration closed",
                icon: FaTimesCircle,
                color: "text-red-400",
                bgColor: "bg-red-900/20",
                borderColor: "border-red-800"
            },
            "completed": {
                message: "Event completed",
                icon: FaCheckCircle,
                color: "text-gray-400",
                bgColor: "bg-gray-900/20",
                borderColor: "border-gray-800"
            }
        };

        return statusConfig[registrationStatus] || {
            message: "Registration status unknown",
            icon: FaExclamationTriangle,
            color: "text-yellow-400",
            bgColor: "bg-yellow-900/20",
            borderColor: "border-yellow-800"
        };
    };

    // Effects
    useEffect(() => {
        if (id) fetchAwardDetails();
    }, [id]);

    useEffect(() => {
        if (!autoPlay || carouselImages.length <= 1) return;
        const interval = setInterval(nextImage, 5000);
        return () => clearInterval(interval);
    }, [autoPlay, carouselImages.length, currentImageIndex]);

    useEffect(() => {
        return () => {
            carouselImages.forEach(image => {
                if (image.url?.startsWith('blob:') && !image.isPlaceholder) {
                    URL.revokeObjectURL(image.url);
                }
            });
        };
    }, [carouselImages]);

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <FaSpinner className="animate-spin text-yellow-500 text-4xl mx-auto mb-4" />
                    <p className="text-white font-medium">Loading award details...</p>
                </div>
            </div>
        );
    }

    // Award not found state
    if (!award) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <FaExclamationTriangle className="text-red-500 text-4xl mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-white mb-2">Award Not Found</h2>
                    <p className="text-gray-300 mb-6">The award you're looking for doesn't exist.</p>
                    <button
                        onClick={handleBackToAwards}
                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 px-6 rounded-xl transition-all duration-300"
                    >
                        Back to Awards Gallery
                    </button>
                </div>
            </div>
        );
    }

    const statusInfo = getRegistrationStatusMessage();
    const StatusIcon = statusInfo.icon;

    return (
        <div className="min-h-screen bg-gray-900">
            {/* Carousel Section - MODIFIED FOR FIRST IMAGE */}
            {carouselImages.length > 0 && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8 }}
                    className="relative w-full h-screen min-h-[300px] max-h-[700px] bg-black overflow-hidden"
                >
                    <AnimatePresence initial={false} custom={direction}>
                        <motion.div
                            key={currentImageIndex}
                            custom={direction}
                            variants={{
                                enter: (direction) => ({ x: direction > 0 ? 1000 : -1000, opacity: 0 }),
                                center: { zIndex: 1, x: 0, opacity: 1 },
                                exit: (direction) => ({ zIndex: 0, x: direction < 0 ? 1000 : -1000, opacity: 0 })
                            }}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.4 } }}
                            className="absolute inset-0"
                        >
                            {/* ALL IMAGES - NO CONTENT OVERLAY */}
                            <motion.img
                                src={carouselImages[currentImageIndex]?.url}
                                alt={carouselImages[currentImageIndex]?.caption || "Award Image"}
                                className="w-full h-full object-continer"
                                initial={{ scale: 1 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 0.5 }}
                                onLoad={() => {
                                    setImageLoading(false);
                                    if (currentImageIndex === 0) {
                                        setFirstImageLoaded(true);
                                    }
                                }}
                                onError={(e) => {
                                    setImageLoading(false);
                                    e.target.src = "https://images.unsplash.com/photo-1511578314322-379afb476865?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80";
                                    if (currentImageIndex === 0) {
                                        setFirstImageLoaded(true);
                                    }
                                }}
                            />

                            {/* NO GRADIENT OVERLAY - REMOVED */}
                            {/* NO CONTENT OVERLAY - REMOVED */}
                        </motion.div>
                    </AnimatePresence>

                    {/* Navigation Arrows */}
                    {carouselImages.length > 1 && (
                        <>
                            <motion.button
                                whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.8)" }}
                                whileTap={{ scale: 0.9 }}
                                onClick={prevImage}
                                className="absolute left-6 top-1/2 transform -translate-y-1/2 bg-black/60 text-white p-4 rounded-full transition-all duration-300 shadow-2xl z-10"
                                aria-label="Previous image"
                            >
                                <FaChevronLeft size={24} className="text-yellow-500" />
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.1, backgroundColor: "rgba(0,0,0,0.8)" }}
                                whileTap={{ scale: 0.9 }}
                                onClick={nextImage}
                                className="absolute right-6 top-1/2 transform -translate-y-1/2 bg-black/60 text-white p-4 rounded-full transition-all duration-300 shadow-2xl z-10"
                                aria-label="Next image"
                            >
                                <FaChevronRight size={24} className="text-yellow-500" />
                            </motion.button>
                        </>
                    )}

                    {/* Loading Overlay */}
                    <AnimatePresence>
                        {imageLoading && (
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="absolute inset-0 bg-black/80 flex items-center justify-center z-20"
                            >
                                <div className="text-center">
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    >
                                        <FaSpinner className="text-yellow-500 text-4xl mx-auto mb-3" />
                                    </motion.div>
                                    <motion.p
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2 }}
                                        className="text-white text-lg"
                                    >
                                        Loading amazing content...
                                    </motion.p>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            )}

            {/* Main Content - ADJUSTED MARGIN FOR FIRST IMAGE */}
            <div className={`max-w-6xl mx-auto p-4 md:p-6 relative z-10 ${firstImageLoaded ? 'mt-0' : ''}`}>
                {/* Award Details Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="bg-black rounded-2xl shadow-lg border border-gray-800 p-8 mb-8 hover:shadow-xl transition-all duration-300"
                >
                    <h2 className="text-2xl font-bold text-white mb-8 flex items-center gap-3">
                        <FaTrophy className="text-yellow-500 text-2xl" />
                        Award Timeline & Details
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                        {[
                            { icon: FaCalendarAlt, label: "Event Starts", value: formatDate(award.awardEventStartDate) },
                            { icon: FaCalendarAlt, label: "Event Ends", value: formatDate(award.awardEventEndDate) },
                            { icon: FaUserPlus, label: "Registration Starts", value: formatDate(award.registrationStartDate) },
                            { icon: FaTimesCircle, label: "Registration Ends", value: formatDate(award.registrationEndDate) }
                        ].map((item, index) => (
                            <motion.div
                                key={item.label}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                whileHover={{ scale: 1.02, y: -2 }}
                                className="flex items-center gap-4 p-6 bg-gray-900 rounded-xl border border-gray-800 transition-all duration-300"
                            >
                                <item.icon className="text-yellow-500 text-2xl flex-shrink-0" />
                                <div>
                                    <p className="text-gray-300 text-sm font-medium">{item.label}</p>
                                    <p className="text-white font-semibold text-lg">{item.value}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Location Information */}
                    {award.location && (
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.3 }}
                            className="bg-gray-900 rounded-xl p-6 border border-gray-800 mb-6"
                        >
                            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                                <FaMapMarkerAlt className="text-yellow-500" />
                                Event Location
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {[
                                    { label: "Venue", value: award.location.venue },
                                    { label: "Address", value: award.location.address },
                                    { label: "City", value: award.location.city },
                                    { label: "State", value: award.location.state },
                                    { label: "Country", value: award.location.country },
                                    { label: "Zip Code", value: award.location.zipCode }
                                ].map((item, index) => (
                                    item.value && (
                                        <motion.div
                                            key={item.label}
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.6, delay: index * 0.1 }}
                                            className="flex justify-between items-center py-3 border-b border-gray-700"
                                        >
                                            <span className="text-gray-300 font-medium">{item.label}:</span>
                                            <span className="text-white font-semibold text-right">{item.value}</span>
                                        </motion.div>
                                    )
                                ))}
                            </div>
                        </motion.div>
                    )}
                </motion.div>

                {/* Registration Form */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                    className="bg-black rounded-2xl shadow-lg border border-gray-800 p-8 hover:shadow-xl transition-all duration-300"
                >
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
                        <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                            <FaUser className="text-yellow-500 text-2xl" />
                            Award Nomination Form
                        </h2>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className={`flex items-center gap-3 px-5 py-3 rounded-xl ${statusInfo.bgColor} border ${statusInfo.borderColor}`}
                        >
                            <StatusIcon className={statusInfo.color} />
                            <span className={`font-semibold ${statusInfo.color}`}>
                                {statusInfo.message}
                            </span>
                        </motion.div>
                    </div>

                    <AnimatePresence mode="wait">
                        {registrationStatus !== "open" ? (
                            <motion.div
                                key="closed"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="text-center py-16"
                            >
                                <StatusIcon className={`text-6xl mx-auto mb-6 ${statusInfo.color}`} />
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    {registrationStatus === "not-started" ? "Registration Not Started" :
                                        registrationStatus === "closed" ? "Registration Closed" :
                                            "Event Completed"}
                                </h3>
                                <p className="text-gray-300 mb-8 max-w-md mx-auto text-lg">
                                    {registrationStatus === "not-started" &&
                                        `Registration will open on ${formatDate(award.registrationStartDate)}. Please check back then.`}
                                    {registrationStatus === "closed" &&
                                        "The registration period for this award has ended. Thank you for your interest."}
                                    {registrationStatus === "completed" &&
                                        "This award event has been completed. Thank you to all participants."}
                                </p>
                                <div className="flex gap-4 justify-center">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleBackToAwards}
                                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-8 rounded-xl transition-all duration-300"
                                    >
                                        Back to Awards Gallery
                                    </motion.button>
                                </div>
                            </motion.div>
                        ) : submitStatus === 'pending' && showPendingMessage ? (
                            <motion.div
                                key="pending"
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.8 }}
                                className="text-center py-16"
                            >
                                <motion.div
                                    animate={{
                                        rotate: 360,
                                        scale: [1, 1.1, 1]
                                    }}
                                    transition={{
                                        rotate: { duration: 2, repeat: Infinity, ease: "linear" },
                                        scale: { duration: 1.5, repeat: Infinity, ease: "easeInOut" }
                                    }}
                                >
                                    <FaHourglassHalf className="text-yellow-500 text-6xl mx-auto mb-6" />
                                </motion.div>
                                <h3 className="text-3xl font-bold text-white mb-4">
                                    Nomination Submitted Successfully!
                                </h3>
                                <p className="text-gray-300 mb-6 max-w-md mx-auto text-lg">
                                    Your nomination for <span className="text-yellow-500 font-semibold">{award.awardFullName || award.awardName}</span> has been submitted and is currently <span className="text-yellow-400 font-bold">pending review</span>.
                                </p>

                                <div className="bg-yellow-900/20 border border-yellow-800 rounded-xl p-6 mb-8 max-w-md mx-auto">
                                    <div className="flex items-center justify-center gap-3 mb-4">
                                        <FaHourglassHalf className="text-yellow-400 text-xl" />
                                        <h4 className="text-yellow-400 font-bold text-xl">Nomination Status: PENDING</h4>
                                    </div>
                                    <p className="text-yellow-300 text-sm mb-3">
                                        ✓ Your nomination has been received
                                    </p>
                                    <p className="text-yellow-300 text-sm mb-3">
                                        ✓ Currently under review by our panel
                                    </p>
                                    <p className="text-yellow-300 text-sm">
                                        ✓ You will be notified via email about the status
                                    </p>
                                </div>

                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.5 }}
                                    className="text-green-400 mb-8 text-lg font-semibold"
                                >
                                    Redirecting to awards gallery in {countdown} seconds...
                                </motion.div>

                                <div className="flex gap-4 justify-center">
                                    <motion.button
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleBackToAwards}
                                        className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-3 px-8 rounded-xl transition-all duration-300"
                                    >
                                        Go to Awards Gallery Now
                                    </motion.button>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onSubmit={handleSubmit}
                                className="space-y-8"
                            >
                                {/* Section 1: Basic Details */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.1 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaUser className="text-yellow-500" />
                                        Basic Details
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {[
                                            { name: "fullName", icon: FaUser, label: "Full Name *", type: "text", placeholder: "Enter your full name", required: true },
                                            { name: "mobileNo", icon: FaPhone, label: "Mobile Number *", type: "tel", placeholder: "Enter your mobile number", required: true },
                                            { name: "email", icon: FaEnvelope, label: "Email Address *", type: "email", placeholder: "Enter your email", required: true },
                                            { name: "city", icon: FaMapMarkerAlt, label: "City *", type: "text", placeholder: "Enter your city", required: true },
                                        ].map((field, index) => (
                                            <motion.div
                                                key={field.name}
                                                initial={{ opacity: 0, y: 20 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                                className="space-y-3"
                                            >
                                                <label className="flex items-center gap-3 text-white font-medium">
                                                    <field.icon className="text-yellow-500" size={16} />
                                                    {field.label}
                                                </label>
                                                <input
                                                    type={field.type}
                                                    name={field.name}
                                                    value={formData[field.name]}
                                                    onChange={handleInputChange}
                                                    required={field.required}
                                                    className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 transition-all duration-300 text-white placeholder-gray-400"
                                                    placeholder={field.placeholder}
                                                />
                                            </motion.div>
                                        ))}
                                    </div>
                                </motion.div>

                                {/* Section 2: Professional Details */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaBriefcase className="text-yellow-500" />
                                        Professional Details
                                    </h3>

                                    {/* Applicant Type Radio Buttons */}
                                    <motion.div className="space-y-4 mb-6">
                                        <label className="block text-white font-medium mb-3">
                                            Applicant Type *
                                        </label>
                                        <div className="flex flex-col md:flex-row gap-4">
                                            {["Business Owner", "Working Professional"].map((type) => (
                                                <motion.label
                                                    key={type}
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${formData.applicantType === type ? 'border-yellow-500 bg-yellow-900/20' : 'border-gray-700 hover:border-yellow-400'}`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="applicantType"
                                                        value={type}
                                                        checked={formData.applicantType === type}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-5 h-5 text-yellow-500 focus:ring-yellow-500 bg-gray-800 border-gray-600"
                                                    />
                                                    <div className="flex items-center gap-3">
                                                        {type === "Business Owner" ?
                                                            <FaBusinessTime className="text-yellow-500" size={20} /> :
                                                            <FaUserTie className="text-yellow-500" size={20} />
                                                        }
                                                        <div>
                                                            <span className="text-white font-medium">{type}</span>
                                                            <p className="text-gray-400 text-sm">
                                                                {type === "Business Owner" ? "You own a business/company" : "You work for an organization"}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </motion.label>
                                            ))}
                                        </div>
                                    </motion.div>

                                    {/* Company/Business Name and Designation - Always Required */}
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: 0.3 }}
                                        className="grid grid-cols-1 md:grid-cols-2 gap-6"
                                    >
                                        <div className="space-y-3">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                <FaBuilding className="text-yellow-500" />
                                                {formData.applicantType === "Business Owner" ? "Business Name *" : "Company/Organisation Name *"}
                                            </label>
                                            <input
                                                type="text"
                                                name="companyName"
                                                value={formData.companyName}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400"
                                                placeholder={formData.applicantType === "Business Owner" ? "Enter your business name" : "Enter your company/organisation name"}
                                            />
                                            <p className="text-gray-400 text-sm">
                                                {formData.applicantType === "Business Owner" ?
                                                    "Enter the name of your business/enterprise" :
                                                    "Enter the name of the company or organisation you work for"}
                                            </p>
                                        </div>

                                        <div className="space-y-3">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                <FaBriefcase className="text-yellow-500" />
                                                Designation *
                                            </label>
                                            <input
                                                type="text"
                                                name="designation"
                                                value={formData.designation}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400"
                                                placeholder={formData.applicantType === "Business Owner" ?
                                                    "E.g., Founder, CEO, Owner, Director" :
                                                    "E.g., Manager, Engineer, Consultant, Executive"}
                                            />
                                            <p className="text-gray-400 text-sm">
                                                {formData.applicantType === "Business Owner" ?
                                                    "Your role/position in the business" :
                                                    "Your job title/position in the organisation"}
                                            </p>
                                        </div>
                                    </motion.div>
                                </motion.div>

                                {/* Section 3: Profile Information */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaUser className="text-yellow-500" />
                                        Profile Information
                                    </h3>

                                    <div className="space-y-6">
                                        {/* About You */}
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <label className="text-white font-medium flex items-center gap-2">
                                                    <FaUser className="text-yellow-500" />
                                                    About You
                                                    <span className="text-gray-400 text-sm font-normal">(Optional)</span>
                                                </label>
                                                <span className={`text-sm ${getWordCount(formData.aboutYou) > 200 ? 'text-red-400' : 'text-gray-400'}`}>
                                                    {getWordCount(formData.aboutYou)} / 200 words
                                                </span>
                                            </div>
                                            <textarea
                                                name="aboutYou"
                                                value={formData.aboutYou}
                                                onChange={handleTextareaChange}
                                                rows="3"
                                                className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400 resize-none"
                                                placeholder="Tell us about yourself, your professional journey, achievements..."
                                            />
                                            <p className="text-gray-400 text-sm">
                                                Maximum 200 words. Share your professional background, key achievements, and experience.
                                            </p>
                                        </div>

                                        {/* About Business/Organisation */}
                                        <div className="space-y-3">
                                            <div className="flex justify-between items-center">
                                                <label className="text-white font-medium flex items-center gap-2">
                                                    <FaBuilding className="text-yellow-500" />
                                                    About {formData.applicantType === "Business Owner" ? "Your Business" : "Your Organisation"}
                                                    <span className="text-gray-400 text-sm font-normal">(Optional)</span>
                                                </label>
                                                <span className={`text-sm ${getWordCount(formData.aboutBusiness) > 200 ? 'text-red-400' : 'text-gray-400'}`}>
                                                    {getWordCount(formData.aboutBusiness)} / 200 words
                                                </span>
                                            </div>
                                            <textarea
                                                name="aboutBusiness"
                                                value={formData.aboutBusiness}
                                                onChange={handleTextareaChange}
                                                rows="3"
                                                className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400 resize-none"
                                                placeholder={formData.applicantType === "Business Owner" ?
                                                    "Describe your business, products/services, achievements..." :
                                                    "Describe your organisation, its mission, values, and achievements..."
                                                }
                                            />
                                            <p className="text-gray-400 text-sm">
                                                Maximum 200 words. Describe the business/organisation, its products/services, achievements, and market position.
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Section 4: Award Category */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.5 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaStar className="text-yellow-500" />
                                        Award Category
                                    </h3>

                                    <div className="space-y-3">
                                        <label className="text-white font-medium flex items-center gap-2">
                                            <FaAward className="text-yellow-500" />
                                            Select Award Category *
                                        </label>

                                        {categoriesLoading ? (
                                            <div className="flex items-center gap-3 p-4 bg-gray-800 border border-gray-700 rounded-xl">
                                                <FaSpinner className="animate-spin text-yellow-500" />
                                                <span className="text-gray-300">Loading award categories...</span>
                                            </div>
                                        ) : awardCategories.length > 0 ? (
                                            <select
                                                name="awardCategory"
                                                value={formData.awardCategory}
                                                onChange={handleInputChange}
                                                required
                                                className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white appearance-none cursor-pointer"
                                            >
                                                <option value="" className="text-gray-400 bg-gray-800">Select an award category</option>
                                                {awardCategories.map((category, index) => (
                                                    <option key={index} value={category} className="text-white bg-gray-800">
                                                        {category}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <div className="p-4 bg-gray-800 border border-gray-700 rounded-xl">
                                                <p className="text-gray-300 text-center">No award categories available</p>
                                            </div>
                                        )}

                                        <p className="text-gray-400 text-sm">
                                            Choose the category that best fits your nomination. Each category has specific criteria.
                                        </p>
                                    </div>
                                </motion.div>

                                {/* Section 5: Award Nomination Details */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaQuestionCircle className="text-yellow-500" />
                                        Award Nomination Details
                                    </h3>

                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                Why should this nominee receive the award? *
                                            </label>
                                            <span className={`text-sm ${getWordCount(formData.nominationReason) > 100 ? 'text-red-400' : 'text-gray-400'}`}>
                                                {getWordCount(formData.nominationReason)} / 100 words
                                            </span>
                                        </div>
                                        <textarea
                                            name="nominationReason"
                                            value={formData.nominationReason}
                                            onChange={handleTextareaChange}
                                            rows="4"
                                            required
                                            className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400 resize-none"
                                            placeholder="Explain why this nominee deserves the award. Highlight specific achievements, impact, innovations, and contributions..."
                                        />
                                        <p className="text-gray-400 text-sm">
                                            Short answer (maximum 100 words). Be specific about achievements, innovations, impact, and why they stand out in the chosen category.
                                        </p>
                                    </div>
                                </motion.div>

                                {/* NEW SECTION: Ethics & Compliance Self-Declaration */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.7 }}
                                    className="bg-gray-900 rounded-xl p-6 border border-gray-800"
                                >
                                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                                        <FaShieldAlt className="text-yellow-500" />
                                        Ethics & Compliance Self-Declaration
                                    </h3>

                                    <div className="space-y-8">
                                        {/* MASA Philosophy Statement */}
                                        <motion.div
                                            initial={{ opacity: 0, x: -20 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ duration: 0.6, delay: 0.1 }}
                                            className="bg-yellow-900/10 border border-yellow-800 rounded-xl p-5"
                                        >
                                            <div className="flex items-start gap-3">
                                                <FaBalanceScale className="text-yellow-500 mt-1 flex-shrink-0" />
                                                <div>
                                                    <h4 className="text-yellow-400 font-bold text-lg mb-2">MASA Philosophy</h4>
                                                    <p className="text-white font-semibold italic mb-2">
                                                        "Real scale comes from character before capital and values before valuation."
                                                    </p>
                                                    <p className="text-gray-300">
                                                        MASA believes in ethical business practices and integrity as the foundation of sustainable growth.
                                                    </p>
                                                </div>
                                            </div>
                                        </motion.div>

                                        {/* Declaration of Ethical Conduct */}
                                        <div className="space-y-4">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                <FaGavel className="text-yellow-500" />
                                                Declaration of Ethical Conduct *
                                            </label>
                                            
                                            <div className="bg-gray-800 border border-gray-700 rounded-xl p-5">
                                                <p className="text-gray-300 mb-4">
                                                    I hereby declare that my enterprise operates with integrity and transparency and does not knowingly engage in fraudulent practices, bribery, tax evasion, misrepresentation of products or services, or violation of applicable laws. I understand that MASA reserves the right to suspend or terminate membership if ethical misconduct is established.
                                                </p>
                                                
                                                <motion.label
                                                    whileHover={{ scale: 1.02 }}
                                                    className="flex items-center gap-4 p-4 bg-gray-900 rounded-xl border border-gray-700 cursor-pointer transition-all duration-300"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        name="ethicsDeclaration"
                                                        checked={formData.ethicsDeclaration}
                                                        onChange={handleInputChange}
                                                        required
                                                        className="w-5 h-5 text-yellow-500 focus:ring-yellow-500 bg-gray-800 border-gray-600 rounded"
                                                    />
                                                    <div className="flex items-center gap-3">
                                                        <FaCheckCircle className="text-green-500" />
                                                        <span className="text-white font-semibold">Yes, I agree to the Declaration of Ethical Conduct</span>
                                                    </div>
                                                </motion.label>
                                            </div>
                                        </div>

                                        {/* Business Philosophy */}
                                        <div className="space-y-4">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                <FaHandshake className="text-yellow-500" />
                                                Business Philosophy *
                                            </label>
                                            <p className="text-gray-400 text-sm mb-3">
                                                Which statement best reflects your business philosophy?
                                            </p>

                                            <div className="space-y-3">
                                                {[
                                                    {
                                                        value: "sustainable",
                                                        label: "Sustainable growth with ethical practices, even if growth is slower",
                                                        description: "Prioritizing integrity and long-term sustainability over rapid expansion"
                                                    },
                                                    {
                                                        value: "fast",
                                                        label: "Fast growth is acceptable even if rules need to be bent",
                                                        description: "Focusing on rapid expansion with flexible approach to regulations"
                                                    },
                                                    {
                                                        value: "evolving",
                                                        label: "Still evolving my understanding of ethical business practices",
                                                        description: "Learning and developing ethical business practices"
                                                    }
                                                ].map((option) => (
                                                    <motion.label
                                                        key={option.value}
                                                        whileHover={{ scale: 1.02 }}
                                                        className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${formData.businessPhilosophy === option.value ? 'border-yellow-500 bg-yellow-900/20' : 'border-gray-700 hover:border-yellow-400'}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="businessPhilosophy"
                                                            value={option.value}
                                                            checked={formData.businessPhilosophy === option.value}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="w-5 h-5 text-yellow-500 focus:ring-yellow-500 bg-gray-800 border-gray-600 mt-1"
                                                        />
                                                        <div>
                                                            <span className="text-white font-medium block mb-1">{option.label}</span>
                                                            <span className="text-gray-400 text-sm">{option.description}</span>
                                                        </div>
                                                    </motion.label>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Regulatory / Legal History */}
                                        <div className="space-y-4">
                                            <label className="text-white font-medium flex items-center gap-2">
                                                <FaBalanceScale className="text-yellow-500" />
                                                Regulatory / Legal History *
                                            </label>
                                            <p className="text-gray-400 text-sm mb-3">
                                                Have you or your enterprise faced any regulatory, financial, or legal action in the last 5 years?
                                            </p>

                                            <div className="flex flex-col md:flex-row gap-4 mb-4">
                                                {[
                                                    { value: "no", label: "No" },
                                                    { value: "yes", label: "Yes" }
                                                ].map((option) => (
                                                    <motion.label
                                                        key={option.value}
                                                        whileHover={{ scale: 1.02 }}
                                                        className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ${formData.regulatoryHistory === option.value ? 'border-yellow-500 bg-yellow-900/20' : 'border-gray-700 hover:border-yellow-400'}`}
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="regulatoryHistory"
                                                            value={option.value}
                                                            checked={formData.regulatoryHistory === option.value}
                                                            onChange={handleInputChange}
                                                            required
                                                            className="w-5 h-5 text-yellow-500 focus:ring-yellow-500 bg-gray-800 border-gray-600"
                                                        />
                                                        <span className="text-white font-medium">{option.label}</span>
                                                    </motion.label>
                                                ))}
                                            </div>

                                            {/* Explanation if Yes is selected */}
                                            {formData.regulatoryHistory === "yes" && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: "auto" }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="space-y-3"
                                                >
                                                    <div className="flex justify-between items-center">
                                                        <label className="text-white font-medium">
                                                            Please briefly explain
                                                        </label>
                                                        <span className={`text-sm ${getWordCount(formData.regulatoryExplanation) > 100 ? 'text-red-400' : 'text-gray-400'}`}>
                                                            {getWordCount(formData.regulatoryExplanation)} / 100 words
                                                        </span>
                                                    </div>
                                                    <textarea
                                                        name="regulatoryExplanation"
                                                        value={formData.regulatoryExplanation}
                                                        onChange={handleTextareaChange}
                                                        rows="3"
                                                        className="w-full px-5 py-4 bg-gray-800 border border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-white placeholder-gray-400 resize-none"
                                                        placeholder="Please provide brief explanation of the regulatory, financial, or legal action..."
                                                        required={formData.regulatoryHistory === "yes"}
                                                    />
                                                    <p className="text-gray-400 text-sm">
                                                        Maximum 100 words. Please provide honest and transparent information.
                                                    </p>
                                                </motion.div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>

                                {/* Error Message */}
                                <AnimatePresence>
                                    {submitStatus === 'error' && (
                                        <motion.div
                                            initial={{ opacity: 0, y: -10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -10 }}
                                            className="flex items-center gap-4 p-5 bg-red-900/20 border border-red-800 rounded-xl"
                                        >
                                            <FaExclamationTriangle className="text-red-400 text-xl flex-shrink-0" />
                                            <div>
                                                <p className="text-red-300 font-semibold">Submission Failed</p>
                                                <p className="text-red-300 text-sm mt-1">{errorMessage}</p>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                {/* reCAPTCHA */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                    className="flex justify-center"
                                >
                                    <ReCAPTCHA
                                        sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                                        onChange={handleCaptchaChange}
                                    />
                                </motion.div>

                                {/* Submit Button */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.9 }}
                                    className="flex gap-5 pt-6"
                                >
                                    <motion.button
                                        type="button"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        onClick={handleBackToAwards}
                                        disabled={isSubmitting}
                                        className="flex-1 px-8 py-4 bg-gray-800 hover:bg-gray-700 text-white font-bold rounded-xl transition-all duration-300 text-lg flex items-center justify-center gap-3"
                                    >
                                        <FaArrowLeft />
                                        Back to Awards
                                    </motion.button>
                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                        disabled={isSubmitting || !formData.awardCategory || !formData.nominationReason || 
                                                 !formData.designation || !formData.ethicsDeclaration || !formData.businessPhilosophy}
                                        className="flex-1 px-8 py-4 bg-yellow-500 hover:bg-yellow-600 disabled:bg-yellow-800 disabled:cursor-not-allowed text-black font-bold rounded-xl transition-all duration-300 transform shadow-lg flex items-center justify-center gap-3 text-lg"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}>
                                                    <FaSpinner />
                                                </motion.div>
                                                Submitting...
                                            </>
                                        ) : (
                                            <>
                                                <FaCheckCircle />
                                                Submit
                                            </>
                                        )}
                                    </motion.button>
                                </motion.div>

                                {/* Form Requirements Note */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 0.6, delay: 1.0 }}
                                    className="text-center pt-6 border-t border-gray-800"
                                >
                                    <p className="text-gray-400 text-sm">
                                        * Required fields must be filled. All information will be kept confidential and used only for award nomination purposes.
                                    </p>
                                </motion.div>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </motion.div>
            </div>

            <Footer />
        </div>
    );
}

export default AwardRegistration;